/**
 * vibecheck missionpack — Scan-driven task breakdowns with progress tracking
 *
 * Enhanced capabilities:
 *   create      — Generate missions from a goal (smart template matching)
 *   from-scan   — Auto-generate fix missions from scan findings
 *   from-polish — Auto-generate improvement missions from polish results
 *   list        — List all mission packs with progress bars
 *   show        — Show detailed task breakdown with status
 *   start       — Mark a task as in-progress
 *   complete    — Mark a task as completed
 *   skip        — Skip a task with reason
 *   progress    — Show overall progress across all missions
 *   export      — Export as markdown, JSON, or GitHub Issues format
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import type { GlobalOptions, MissionPack, MissionTask, ScanFinding } from '../types';
import { detectProject } from '../core/project-detect';
import { ensureVibecheckDir } from '../core/config';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

// ── Types ───────────────────────────────────────────────────────────────────

interface MissionOpts extends GlobalOptions {
  goal?: string;
  complexity?: string;
  format?: string;
  output?: string;
  severity?: string;
  category?: string;
}

// ── Registration ────────────────────────────────────────────────────────────

export function registerMissionpack(program: Command): void {
  const cmd = program
    .command('missionpack')
    .description('Scan-driven task breakdowns with progress tracking')
    .option('--goal <description>', 'The feature/goal to plan')
    .option('--complexity <level>', 'simple, moderate, complex', 'moderate')
    .option('--format <type>', 'Output format: pretty, json, markdown, github', 'pretty')
    .option('--output <path>', 'Save to file')
    .option('--severity <level>', 'Min severity for scan missions: critical, high, medium, low')
    .option('--category <cat>', 'Filter scan findings by category');

  cmd.command('create').description('Create a new mission pack from a goal').action(async (_o: any, c: Command) => {
    const globals = c.parent!.parent!.opts() as GlobalOptions;
    const parentOpts = c.parent!.opts() as any;
    await runCreate({ ...globals, ...parentOpts });
  });

  cmd.command('from-scan').description('Auto-generate fix missions from last scan findings').action(async (_o: any, c: Command) => {
    const globals = c.parent!.parent!.opts() as GlobalOptions;
    const parentOpts = c.parent!.opts() as any;
    await runFromScan({ ...globals, ...parentOpts });
  });

  cmd.command('from-polish').description('Auto-generate improvement missions from polish results').action(async (_o: any, c: Command) => {
    const globals = c.parent!.parent!.opts() as GlobalOptions;
    const parentOpts = c.parent!.opts() as any;
    await runFromPolish({ ...globals, ...parentOpts });
  });

  cmd.command('list').description('List existing mission packs with progress').action(async (_o: any, c: Command) => {
    await runList(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('show <id>').description('Show detailed task breakdown with status').action(async (id: string, _o: any, c: Command) => {
    await runShow(c.parent!.parent!.opts() as GlobalOptions, id);
  });

  cmd.command('start <id> <taskNum>').description('Mark a task as in-progress').action(async (id: string, taskNum: string, _o: any, c: Command) => {
    await runUpdateTask(c.parent!.parent!.opts() as GlobalOptions, id, parseInt(taskNum), 'in_progress');
  });

  cmd.command('complete <id> <taskNum>').description('Mark a task as completed').action(async (id: string, taskNum: string, _o: any, c: Command) => {
    await runUpdateTask(c.parent!.parent!.opts() as GlobalOptions, id, parseInt(taskNum), 'completed');
  });

  cmd.command('skip <id> <taskNum>').description('Skip a task').action(async (id: string, taskNum: string, _o: any, c: Command) => {
    await runUpdateTask(c.parent!.parent!.opts() as GlobalOptions, id, parseInt(taskNum), 'skipped');
  });

  cmd.command('progress').description('Show overall progress across all missions').action(async (_o: any, c: Command) => {
    await runProgress(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('export <id>').description('Export as markdown, JSON, or GitHub Issues format').action(async (id: string, _o: any, c: Command) => {
    const globals = c.parent!.parent!.opts() as GlobalOptions;
    const parentOpts = c.parent!.opts() as any;
    await runExport({ ...globals, ...parentOpts }, id);
  });

  cmd.action(async (opts: any, command: Command) => {
    const globals = command.parent!.opts() as GlobalOptions;
    await runCreate({ ...globals, ...opts });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// CREATE — Goal-based mission generation
// ═══════════════════════════════════════════════════════════════════════════════

async function runCreate(opts: MissionOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const goal = opts.goal;

  out.brandHeader('Mission Pack', 'Task breakdown generator');
  out.nl();

  if (!goal) {
    out.errorWithFix(
      'No goal specified',
      'Mission packs need a goal to plan around.',
      `vibecheck missionpack create --goal "Add user authentication with OAuth"`
    );
    out.nl();
    console.log(colors.bold('  Or generate from scan results:'));
    console.log(`    ${colors.info('vibecheck missionpack from-scan')}    — Fix missions from scan findings`);
    console.log(`    ${colors.info('vibecheck missionpack from-polish')} — Improvement missions from polish`);
    process.exitCode = 3;
    return;
  }

  const project = detectProject(rootPath);
  out.info(`Analyzing project: ${colors.bold(project.name)} (${project.framework})`);
  out.info(`Goal: ${colors.bold(goal)}`);
  out.nl();

  const pack = generateMissionPack(goal, project, opts.complexity || 'moderate');
  saveMissionPack(rootPath, pack);

  if (opts.json || opts.format === 'json') { out.jsonOutput(pack); return; }
  renderMissionPack(pack);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FROM-SCAN — Auto-generate missions from scan findings
// ═══════════════════════════════════════════════════════════════════════════════

async function runFromScan(opts: MissionOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Mission Pack', 'From Scan Findings');
  out.nl();

  // Load scan results
  const scanPath = path.join(rootPath, '.vibecheck', 'scan-results.json');
  if (!fs.existsSync(scanPath)) {
    out.warn('No scan results found. Run a scan first:');
    console.log(`  ${colors.info('vibecheck scan')}`);
    return;
  }

  let findings: ScanFinding[];
  try {
    const data = JSON.parse(fs.readFileSync(scanPath, 'utf-8'));
    findings = data.findings || [];
  } catch {
    out.error('Could not parse scan results.');
    return;
  }

  if (findings.length === 0) {
    out.success('No findings — nothing to fix!');
    return;
  }

  // Filter by severity/category
  const minSeverity = opts.severity || 'medium';
  const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];
  const minIdx = severityOrder.indexOf(minSeverity);
  if (minIdx >= 0) {
    findings = findings.filter(f => severityOrder.indexOf(f.severity) <= minIdx);
  }
  if (opts.category) {
    findings = findings.filter(f => f.category === opts.category);
  }

  out.info(`Found ${findings.length} findings to generate missions from`);
  out.nl();

  // Group findings by category + file
  const byCategory = new Map<string, ScanFinding[]>();
  for (const f of findings) {
    const key = f.category || 'other';
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key)!.push(f);
  }

  const tasks: MissionTask[] = [];
  let taskId = 1;

  // Generate tasks per category
  for (const [category, catFindings] of byCategory) {
    // Group by file within category
    const byFile = new Map<string, ScanFinding[]>();
    for (const f of catFindings) {
      const key = f.file || 'unknown';
      if (!byFile.has(key)) byFile.set(key, []);
      byFile.get(key)!.push(f);
    }

    // Create a task per file group (or merge small groups)
    const fileGroups = [...byFile.entries()].sort((a, b) => b[1].length - a[1].length);

    for (const [file, fileFindings] of fileGroups) {
      const criticals = fileFindings.filter(f => f.severity === 'critical');
      const highs = fileFindings.filter(f => f.severity === 'high');
      const complexity: MissionTask['complexity'] = criticals.length > 0 ? 'complex' : highs.length > 0 ? 'moderate' : 'simple';

      const steps = fileFindings.slice(0, 8).map(f => {
        const fixHint = f.fix ? ` — ${f.fix}` : '';
        return `Fix ${f.ruleId || f.severity}: ${f.message.slice(0, 80)}${fixHint}`;
      });
      if (fileFindings.length > 8) {
        steps.push(`... and ${fileFindings.length - 8} more issues`);
      }

      tasks.push({
        id: taskId++,
        title: `Fix ${fileFindings.length} ${category} issue${fileFindings.length !== 1 ? 's' : ''} in ${path.basename(file)}`,
        complexity,
        files: [file],
        dependsOn: [],
        steps,
        acceptance: [`All ${category} findings resolved in ${path.basename(file)}`, 'No new findings introduced'],
        status: 'pending',
        source: 'scan',
        findingIds: fileFindings.map(f => f.id || f.ruleId).filter(Boolean),
      });
    }
  }

  if (tasks.length === 0) {
    out.success('No actionable findings to create missions from.');
    return;
  }

  // Add dependency chains: critical tasks first
  const criticalTasks = tasks.filter(t => t.complexity === 'complex').map(t => t.id);
  for (const task of tasks) {
    if (task.complexity !== 'complex' && criticalTasks.length > 0) {
      task.dependsOn = criticalTasks.slice(0, 1);
    }
  }

  const complexityScore = tasks.reduce((sum, t) => sum + (t.complexity === 'simple' ? 1 : t.complexity === 'moderate' ? 2 : 3), 0);
  const totalComplexity = complexityScore <= 4 ? 'Simple' : complexityScore <= 8 ? 'Medium' : 'Complex';

  const pack: MissionPack = {
    id: `scan-fixes-${Date.now().toString(36)}`,
    title: `Fix ${findings.length} scan findings`,
    objective: `Resolve all ${findings.length} findings from the last scan across ${byCategory.size} categories`,
    tasks,
    createdAt: new Date().toISOString(),
    totalComplexity,
    source: 'scan',
    progress: { completed: 0, total: tasks.length, percentage: 0 },
  };

  saveMissionPack(rootPath, pack);

  if (opts.json || opts.format === 'json') { out.jsonOutput(pack); return; }
  renderMissionPack(pack);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FROM-POLISH — Auto-generate missions from polish suggestions
// ═══════════════════════════════════════════════════════════════════════════════

async function runFromPolish(opts: MissionOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Mission Pack', 'From Polish Suggestions');
  out.nl();

  // Try to load polish results (run polish --json and capture, or check cached)
  const polishPath = path.join(rootPath, '.vibecheck', 'polish-results.json');
  let polishData: any = null;

  if (fs.existsSync(polishPath)) {
    try { polishData = JSON.parse(fs.readFileSync(polishPath, 'utf-8')); } catch { /* ignore */ }
  }

  if (!polishData || !polishData.results || polishData.results.length === 0) {
    out.warn('No polish results found. Run polish first:');
    console.log(`  ${colors.info('vibecheck polish --json > .vibecheck/polish-results.json')}`);
    console.log(`  ${colors.dim('Or run:')} ${colors.info('vibecheck polish')}`);
    return;
  }

  const results: { file: string; items: { type: string; severity: string; description: string; line: number; autoFixable: boolean }[] }[] = polishData.results;
  const tasks: MissionTask[] = [];
  let taskId = 1;

  // Group by category across files
  const byCat = new Map<string, { file: string; description: string; line: number; autoFixable: boolean }[]>();
  for (const result of results) {
    for (const item of (result.items || [])) {
      const cat = item.type || 'quality';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push({ file: result.file, description: item.description, line: item.line, autoFixable: item.autoFixable });
    }
  }

  for (const [category, items] of byCat) {
    const autoFixable = items.filter(i => i.autoFixable);
    const manual = items.filter(i => !i.autoFixable);

    // Auto-fixable task
    if (autoFixable.length > 0) {
      const uniqueFiles = [...new Set(autoFixable.map(i => i.file))];
      tasks.push({
        id: taskId++,
        title: `Auto-fix ${autoFixable.length} ${category} issues`,
        complexity: 'simple',
        files: uniqueFiles.slice(0, 10),
        dependsOn: [],
        steps: [
          `Run: vibecheck polish --apply --categories ${category}`,
          'Review applied changes',
          'Run tests to verify no regressions',
        ],
        acceptance: [`All auto-fixable ${category} issues resolved`, 'Tests still pass'],
        status: 'pending',
        source: 'polish',
      });
    }

    // Manual task
    if (manual.length > 0) {
      const uniqueFiles = [...new Set(manual.map(i => i.file))];
      const steps = manual.slice(0, 8).map(i => `L${i.line} in ${path.basename(i.file)}: ${i.description.slice(0, 70)}`);
      if (manual.length > 8) steps.push(`... and ${manual.length - 8} more`);

      tasks.push({
        id: taskId++,
        title: `Manually fix ${manual.length} ${category} issues`,
        complexity: manual.length > 10 ? 'complex' : manual.length > 3 ? 'moderate' : 'simple',
        files: uniqueFiles.slice(0, 10),
        dependsOn: autoFixable.length > 0 ? [taskId - 2] : [],
        steps,
        acceptance: [`All ${category} issues resolved`, 'Code quality improved'],
        status: 'pending',
        source: 'polish',
      });
    }
  }

  if (tasks.length === 0) {
    out.success('No polish suggestions to create missions from.');
    return;
  }

  const complexityScore = tasks.reduce((sum, t) => sum + (t.complexity === 'simple' ? 1 : t.complexity === 'moderate' ? 2 : 3), 0);
  const totalComplexity = complexityScore <= 4 ? 'Simple' : complexityScore <= 8 ? 'Medium' : 'Complex';
  const totalItems = results.reduce((s, r) => s + (r.items?.length || 0), 0);

  const pack: MissionPack = {
    id: `polish-fixes-${Date.now().toString(36)}`,
    title: `Polish ${totalItems} code quality issues`,
    objective: `Improve code quality by resolving ${totalItems} suggestions across ${byCat.size} categories`,
    tasks,
    createdAt: new Date().toISOString(),
    totalComplexity,
    source: 'polish',
    progress: { completed: 0, total: tasks.length, percentage: 0 },
  };

  saveMissionPack(rootPath, pack);

  if (opts.json || opts.format === 'json') { out.jsonOutput(pack); return; }
  renderMissionPack(pack);
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIST — Show all mission packs with progress
// ═══════════════════════════════════════════════════════════════════════════════

async function runList(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const missionsDir = path.join(rootPath, '.vibecheck', 'missions');

  out.brandHeader('Mission Packs', 'All plans');
  out.nl();

  if (!fs.existsSync(missionsDir)) {
    out.info('No mission packs found. Create one:');
    console.log(`  ${colors.info('vibecheck missionpack create --goal "Your feature goal"')}`);
    console.log(`  ${colors.info('vibecheck missionpack from-scan')}  — From scan findings`);
    console.log(`  ${colors.info('vibecheck missionpack from-polish')} — From polish suggestions`);
    return;
  }

  const files = fs.readdirSync(missionsDir).filter(f => f.endsWith('.json'));
  if (files.length === 0) {
    out.info('No mission packs found.');
    return;
  }

  if (opts.json) {
    const packs = files.map(f => { try { return JSON.parse(fs.readFileSync(path.join(missionsDir, f), 'utf-8')); } catch { return null; } }).filter(Boolean);
    out.jsonOutput({ packs, total: packs.length });
    return;
  }

  for (const file of files) {
    try {
      const pack: MissionPack = JSON.parse(fs.readFileSync(path.join(missionsDir, file), 'utf-8'));
      const progress = computeProgress(pack);
      const progressBar = renderProgressBar(progress.percentage, 20);
      const sourceTag = pack.source ? colors.dim(` [${pack.source}]`) : '';
      console.log(`  ${icons.package} ${colors.bold(pack.id)}${sourceTag}`);
      console.log(`    ${pack.title} (${pack.tasks.length} tasks, ${pack.totalComplexity})`);
      console.log(`    ${progressBar} ${progress.completed}/${progress.total} tasks (${progress.percentage}%)`);
      out.nl();
    } catch { /* skip invalid */ }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHOW — Detailed task breakdown with status
// ═══════════════════════════════════════════════════════════════════════════════

async function runShow(opts: GlobalOptions, id: string): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const pack = loadMissionPack(rootPath, id);

  if (!pack) {
    out.error(`Mission pack '${id}' not found.`, `Run 'vibecheck missionpack list' to see available packs.`);
    process.exitCode = 3;
    return;
  }

  if (opts.json) { out.jsonOutput(pack); return; }

  const progress = computeProgress(pack);

  out.brandHeader(`Mission: ${pack.title}`);
  out.nl();
  console.log(`  Objective: ${pack.objective}`);
  console.log(`  Tasks: ${pack.tasks.length} · Complexity: ${pack.totalComplexity}`);
  if (pack.source) console.log(`  Source: ${pack.source}`);
  console.log(`  Created: ${pack.createdAt}`);
  out.nl();

  // Progress bar
  console.log(`  ${renderProgressBar(progress.percentage, 30)} ${progress.percentage}%`);
  console.log(`  ${colors.success(`${progress.completed} completed`)} ${colors.dim('·')} ${colors.warning(`${progress.inProgress} in progress`)} ${colors.dim('·')} ${colors.dim(`${progress.pending} pending`)}`);
  out.nl();

  for (const task of pack.tasks) {
    const statusIcon = task.status === 'completed' ? colors.success('✓')
      : task.status === 'in_progress' ? colors.warning('▶')
      : task.status === 'skipped' ? colors.dim('⊘')
      : colors.dim('○');
    const complexityColor = task.complexity === 'simple' ? colors.success : task.complexity === 'moderate' ? colors.warning : colors.error;
    const strikethrough = task.status === 'completed' || task.status === 'skipped';

    console.log(`  ${statusIcon} ${colors.bold(`Task ${task.id}:`)} ${strikethrough ? colors.dim(task.title) : task.title}  ${complexityColor(`[${task.complexity}]`)}`);

    if (task.files.length > 0) {
      console.log(`    Files: ${colors.dim(task.files.slice(0, 5).join(', '))}${task.files.length > 5 ? colors.dim(` +${task.files.length - 5}`) : ''}`);
    }
    if (task.dependsOn.length > 0) {
      console.log(`    Depends on: Task ${task.dependsOn.join(', ')}`);
    }

    if (task.status !== 'completed' && task.status !== 'skipped') {
      for (let i = 0; i < Math.min(task.steps.length, 5); i++) {
        console.log(`    ${i + 1}. ${task.steps[i]}`);
      }
      if (task.steps.length > 5) console.log(`    ${colors.dim(`... +${task.steps.length - 5} more steps`)}`);
    }

    if (task.completedAt) console.log(`    ${colors.dim(`Completed: ${task.completedAt}`)}`);
    out.nl();
  }

  // Next action hint
  const nextTask = pack.tasks.find(t => t.status === 'pending' || t.status === 'in_progress');
  if (nextTask) {
    const action = nextTask.status === 'in_progress' ? 'complete' : 'start';
    console.log(`  Next: ${colors.info(`vibecheck missionpack ${action} ${pack.id} ${nextTask.id}`)}`);
    out.nl();
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPDATE TASK — Start, complete, skip
// ═══════════════════════════════════════════════════════════════════════════════

async function runUpdateTask(opts: GlobalOptions, id: string, taskNum: number, status: 'in_progress' | 'completed' | 'skipped'): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const pack = loadMissionPack(rootPath, id);

  if (!pack) {
    out.error(`Mission pack '${id}' not found.`);
    process.exitCode = 3;
    return;
  }

  const task = pack.tasks.find(t => t.id === taskNum);
  if (!task) {
    out.error(`Task ${taskNum} not found in mission pack '${id}'.`);
    process.exitCode = 3;
    return;
  }

  task.status = status;
  if (status === 'completed') task.completedAt = new Date().toISOString();
  pack.progress = computeProgress(pack);
  pack.updatedAt = new Date().toISOString();

  saveMissionPack(rootPath, pack);

  const statusLabel = status === 'in_progress' ? 'started' : status === 'completed' ? 'completed' : 'skipped';
  const statusIcon = status === 'in_progress' ? '▶' : status === 'completed' ? '✓' : '⊘';

  if (opts.json) {
    out.jsonOutput({ id, taskNum, status, progress: pack.progress });
    return;
  }

  out.success(`${statusIcon} Task ${taskNum} ${statusLabel}: ${task.title}`);
  console.log(`  Progress: ${renderProgressBar(pack.progress!.percentage, 20)} ${pack.progress!.percentage}%`);

  // Show next task
  const nextTask = pack.tasks.find(t => t.status === 'pending' || (t.status === 'in_progress' && t.id !== taskNum));
  if (nextTask) {
    console.log(`  Next: ${colors.info(`vibecheck missionpack start ${id} ${nextTask.id}`)} — ${nextTask.title}`);
  } else if (pack.progress!.percentage === 100) {
    out.nl();
    out.success('🎉 All tasks completed! Mission accomplished!');
  }
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS — Overall progress across all missions
// ═══════════════════════════════════════════════════════════════════════════════

async function runProgress(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const missionsDir = path.join(rootPath, '.vibecheck', 'missions');

  out.brandHeader('Mission Progress', 'All missions');
  out.nl();

  if (!fs.existsSync(missionsDir)) {
    out.info('No mission packs found.');
    return;
  }

  const files = fs.readdirSync(missionsDir).filter(f => f.endsWith('.json'));
  let totalTasks = 0;
  let totalCompleted = 0;
  let totalInProgress = 0;

  const packs: { pack: MissionPack; progress: ReturnType<typeof computeProgress> }[] = [];

  for (const file of files) {
    try {
      const pack: MissionPack = JSON.parse(fs.readFileSync(path.join(missionsDir, file), 'utf-8'));
      const progress = computeProgress(pack);
      packs.push({ pack, progress });
      totalTasks += progress.total;
      totalCompleted += progress.completed;
      totalInProgress += progress.inProgress;
    } catch { /* skip */ }
  }

  if (packs.length === 0) {
    out.info('No mission packs found.');
    return;
  }

  if (opts.json) {
    out.jsonOutput({
      missions: packs.length,
      totalTasks,
      totalCompleted,
      totalInProgress,
      overallPercentage: totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0,
      packs: packs.map(p => ({ id: p.pack.id, title: p.pack.title, ...p.progress })),
    });
    return;
  }

  // Overall progress
  const overallPct = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
  console.log(`  Overall: ${renderProgressBar(overallPct, 30)} ${overallPct}%`);
  console.log(`  ${colors.success(`${totalCompleted} completed`)} ${colors.dim('·')} ${colors.warning(`${totalInProgress} in progress`)} ${colors.dim('·')} ${colors.dim(`${totalTasks - totalCompleted - totalInProgress} pending`)}`);
  out.nl();

  // Per-mission
  for (const { pack, progress } of packs) {
    const bar = renderProgressBar(progress.percentage, 15);
    const sourceTag = pack.source ? colors.dim(` [${pack.source}]`) : '';
    console.log(`  ${bar} ${colors.bold(pack.id)}${sourceTag} — ${progress.completed}/${progress.total}`);
  }
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORT — Markdown, JSON, GitHub Issues
// ═══════════════════════════════════════════════════════════════════════════════

async function runExport(opts: MissionOpts, id: string): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const pack = loadMissionPack(rootPath, id);

  if (!pack) {
    out.error(`Mission pack '${id}' not found.`);
    process.exitCode = 3;
    return;
  }

  if (opts.format === 'json' || opts.json) {
    out.jsonOutput(pack);
    return;
  }

  if (opts.format === 'github') {
    // GitHub Issues format
    const issues = pack.tasks.map(t => {
      const labels = [t.complexity, t.source || 'manual'].filter(Boolean);
      const body = [
        `## ${t.title}`,
        '',
        `**Complexity:** ${t.complexity}`,
        t.files.length > 0 ? `**Files:** ${t.files.join(', ')}` : '',
        t.dependsOn.length > 0 ? `**Depends on:** #${t.dependsOn.join(', #')}` : '',
        '',
        '### Steps',
        ...t.steps.map((s, i) => `- [ ] ${s}`),
        '',
        '### Acceptance Criteria',
        ...t.acceptance.map(a => `- [ ] ${a}`),
      ].filter(Boolean).join('\n');

      return { title: `[Mission] ${t.title}`, body, labels };
    });

    const output = JSON.stringify(issues, null, 2);
    if (opts.output) {
      fs.writeFileSync(opts.output, output, 'utf-8');
      out.success(`Exported ${issues.length} GitHub Issues to ${opts.output}`);
    } else {
      console.log(output);
    }
    return;
  }

  // Markdown export
  const progress = computeProgress(pack);
  const md = [
    `# ${pack.title}`,
    '',
    `**Objective:** ${pack.objective}`,
    `**Tasks:** ${pack.tasks.length} · **Complexity:** ${pack.totalComplexity} · **Progress:** ${progress.percentage}%`,
    pack.source ? `**Source:** ${pack.source}` : '',
    '',
    ...pack.tasks.flatMap(t => {
      const statusMark = t.status === 'completed' ? '✅' : t.status === 'in_progress' ? '▶️' : t.status === 'skipped' ? '⊘' : '⬜';
      return [
        `## ${statusMark} Task ${t.id}: ${t.title} [${t.complexity}]`,
        '',
        t.files.length > 0 ? `**Files:** ${t.files.join(', ')}` : '',
        t.dependsOn.length > 0 ? `**Depends on:** Task ${t.dependsOn.join(', ')}` : '',
        '',
        '**Steps:**',
        ...t.steps.map((s, i) => `${i + 1}. ${s}`),
        '',
        '**Acceptance:**',
        ...t.acceptance.map(a => `- [ ] ${a}`),
        '',
      ];
    }),
  ].filter(l => l !== undefined).join('\n');

  if (opts.output) {
    fs.writeFileSync(opts.output, md, 'utf-8');
    out.success(`Exported to ${opts.output}`);
  } else {
    console.log(md);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function saveMissionPack(rootPath: string, pack: MissionPack): string {
  const missionsDir = path.join(ensureVibecheckDir(rootPath), 'missions');
  if (!fs.existsSync(missionsDir)) fs.mkdirSync(missionsDir, { recursive: true });
  const filePath = path.join(missionsDir, `${pack.id}.json`);
  pack.progress = computeProgress(pack);
  fs.writeFileSync(filePath, JSON.stringify(pack, null, 2), 'utf-8');
  return filePath;
}

function loadMissionPack(rootPath: string, id: string): MissionPack | null {
  const filePath = path.join(rootPath, '.vibecheck', 'missions', `${id}.json`);
  if (!fs.existsSync(filePath)) return null;
  try { return JSON.parse(fs.readFileSync(filePath, 'utf-8')); } catch { return null; }
}

function computeProgress(pack: MissionPack): { completed: number; inProgress: number; pending: number; skipped: number; total: number; percentage: number } {
  const completed = pack.tasks.filter(t => t.status === 'completed').length;
  const inProgress = pack.tasks.filter(t => t.status === 'in_progress').length;
  const skipped = pack.tasks.filter(t => t.status === 'skipped').length;
  const pending = pack.tasks.length - completed - inProgress - skipped;
  const percentage = pack.tasks.length > 0 ? Math.round((completed / pack.tasks.length) * 100) : 0;
  return { completed, inProgress, pending, skipped, total: pack.tasks.length, percentage };
}

function renderProgressBar(percentage: number, width: number): string {
  const filled = Math.round((percentage / 100) * width);
  const empty = width - filled;
  const color = percentage >= 80 ? colors.success : percentage >= 40 ? colors.warning : colors.error;
  return `${color('█'.repeat(filled))}${colors.dim('░'.repeat(empty))}`;
}

function renderMissionPack(pack: MissionPack): void {
  const progress = computeProgress(pack);

  console.log(`${icons.package} ${colors.bold(`Mission Pack: ${pack.title}`)}`);
  if (pack.source) console.log(`  ${colors.dim(`Source: ${pack.source}`)}`);
  console.log(out.divider());
  out.nl();
  console.log(`  Objective: ${pack.objective}`);
  console.log(`  ${renderProgressBar(progress.percentage, 20)} ${progress.percentage}%`);
  out.nl();

  for (const task of pack.tasks) {
    const statusIcon = task.status === 'completed' ? colors.success('✓')
      : task.status === 'in_progress' ? colors.warning('▶')
      : colors.dim('○');
    const complexityColor = task.complexity === 'simple' ? colors.success : task.complexity === 'moderate' ? colors.warning : colors.error;

    console.log(`  ${statusIcon} ${colors.bold(`Task ${task.id}/${pack.tasks.length}:`)} ${task.title}  ${complexityColor(`[${task.complexity}]`)}`);
    if (task.files.length > 0) {
      console.log(`    Files: ${colors.dim(task.files.slice(0, 5).join(', '))}`);
    }
    if (task.dependsOn.length > 0) {
      console.log(`    Depends on: Task ${task.dependsOn.join(', ')}`);
    }
    for (let i = 0; i < Math.min(task.steps.length, 5); i++) {
      console.log(`    ${i + 1}. ${task.steps[i]}`);
    }
    if (task.steps.length > 5) console.log(`    ${colors.dim(`... +${task.steps.length - 5} more steps`)}`);
    out.nl();
  }

  console.log(out.divider());
  console.log(`  Total: ${pack.tasks.length} tasks ${colors.dim('·')} Complexity: ${pack.totalComplexity}`);

  const nextTask = pack.tasks.find(t => !t.status || t.status === 'pending');
  if (nextTask) {
    console.log(`  Start: ${colors.info(`vibecheck missionpack start ${pack.id} ${nextTask.id}`)}`);
  }
  out.nl();
}

// ── Mission Generation Logic ────────────────────────────────────────────────

function generateMissionPack(goal: string, project: any, complexity: string): MissionPack {
  const id = goal.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);

  // Detect what kind of feature this is
  const isAuth = /auth|login|signup|oauth|session|jwt/i.test(goal);
  const isApi = /api|endpoint|route|rest|graphql/i.test(goal);
  const isUI = /component|page|form|dashboard|ui|modal|dialog/i.test(goal);
  const isDB = /database|schema|model|migration|prisma|drizzle/i.test(goal);
  const isTest = /test|testing|coverage|e2e|integration/i.test(goal);
  const isDeploy = /deploy|ci|cd|pipeline|docker|kubernetes|k8s|vercel|netlify|railway/i.test(goal);
  const isMonitor = /monitor|logging|observ|alert|metric|trace|sentry|datadog/i.test(goal);
  const isPerf = /performance|optimize|speed|cache|lazy|bundle|lighthouse/i.test(goal);

  const tasks: MissionTask[] = [];
  let taskId = 1;

  if (isAuth) {
    tasks.push(
      { id: taskId++, title: 'Set up authentication provider', complexity: 'moderate', files: ['src/lib/auth.ts', '.env'], dependsOn: [], steps: ['Install auth packages', 'Configure provider credentials', 'Set up auth route handler', 'Add session management'], acceptance: ['Auth provider configured', 'Login/logout works'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Create auth UI components', complexity: 'simple', files: ['src/components/LoginButton.tsx', 'src/components/UserMenu.tsx'], dependsOn: [1], steps: ['Create sign-in button', 'Create user menu with avatar', 'Add loading states'], acceptance: ['UI renders correctly', 'Sign in/out flow works'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Protect routes and API endpoints', complexity: 'moderate', files: ['src/middleware.ts', 'src/lib/auth-guard.ts'], dependsOn: [1], steps: ['Create auth middleware', 'Apply to protected routes', 'Handle unauthorized responses'], acceptance: ['Protected routes redirect to login', 'API returns 401 for unauthenticated'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add session management and persistence', complexity: 'complex', files: ['src/lib/session.ts'], dependsOn: [1, 3], steps: ['Configure session storage', 'Add session refresh logic', 'Implement remember-me'], acceptance: ['Sessions persist across page reloads', 'Expired sessions redirect to login'], status: 'pending', source: 'goal' },
    );
  } else if (isApi) {
    tasks.push(
      { id: taskId++, title: 'Define API schema and types', complexity: 'simple', files: ['src/types/api.ts'], dependsOn: [], steps: ['Define request/response types', 'Add validation schemas (zod/yup)', 'Document API contract'], acceptance: ['Types compile', 'Schemas validate correctly'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Implement API route handlers', complexity: 'moderate', files: ['src/api/'], dependsOn: [1], steps: ['Create route handler', 'Add input validation', 'Implement business logic', 'Add error handling'], acceptance: ['Endpoints return correct data', 'Validation rejects bad input'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add middleware and auth', complexity: 'moderate', files: ['src/middleware/'], dependsOn: [2], steps: ['Add rate limiting', 'Add authentication check', 'Add request logging', 'Add CORS configuration'], acceptance: ['Rate limiting works', 'Auth required on protected routes'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Write API tests', complexity: 'moderate', files: ['src/api/__tests__/'], dependsOn: [2], steps: ['Write unit tests for handlers', 'Write integration tests', 'Test error cases', 'Test rate limiting'], acceptance: ['All tests pass', 'Edge cases covered'], status: 'pending', source: 'goal' },
    );
  } else if (isUI) {
    tasks.push(
      { id: taskId++, title: 'Create component structure', complexity: 'simple', files: ['src/components/'], dependsOn: [], steps: ['Create component file', 'Define props interface', 'Add basic layout', 'Set up Storybook story (if applicable)'], acceptance: ['Component renders', 'Props are typed'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Implement component logic', complexity: 'moderate', files: ['src/components/', 'src/hooks/'], dependsOn: [1], steps: ['Add state management', 'Implement event handlers', 'Add data fetching if needed', 'Handle edge cases'], acceptance: ['Logic works correctly', 'State updates properly'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Style and polish', complexity: 'simple', files: ['src/components/'], dependsOn: [2], steps: ['Add responsive styles', 'Add animations/transitions', 'Handle loading/error/empty states', 'Add accessibility (ARIA)'], acceptance: ['Looks good on all screen sizes', 'Loading states shown', 'Accessible'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Write tests', complexity: 'simple', files: ['src/components/__tests__/'], dependsOn: [2], steps: ['Write render tests', 'Test user interactions', 'Test edge cases', 'Test accessibility'], acceptance: ['All tests pass', 'Good coverage'], status: 'pending', source: 'goal' },
    );
  } else if (isTest) {
    tasks.push(
      { id: taskId++, title: 'Set up testing infrastructure', complexity: 'moderate', files: ['vitest.config.ts', 'jest.config.ts', 'playwright.config.ts'], dependsOn: [], steps: ['Choose test framework', 'Configure test runner', 'Set up test utilities and mocks', 'Add test scripts to package.json'], acceptance: ['Test runner works', 'Tests can be run with single command'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Write unit tests', complexity: 'moderate', files: ['src/__tests__/', 'tests/unit/'], dependsOn: [1], steps: ['Identify critical functions', 'Write tests for core logic', 'Add edge case tests', 'Aim for 80%+ coverage'], acceptance: ['All unit tests pass', 'Coverage meets target'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Write integration tests', complexity: 'moderate', files: ['tests/integration/'], dependsOn: [1], steps: ['Test API endpoints', 'Test database operations', 'Test auth flows', 'Test error handling'], acceptance: ['Integration tests pass', 'No flaky tests'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add CI test pipeline', complexity: 'simple', files: ['.github/workflows/'], dependsOn: [2, 3], steps: ['Add test step to CI', 'Add coverage reporting', 'Set up test result artifacts'], acceptance: ['Tests run on every PR', 'Coverage reported'], status: 'pending', source: 'goal' },
    );
  } else if (isDB) {
    tasks.push(
      { id: taskId++, title: 'Design database schema', complexity: 'moderate', files: ['prisma/schema.prisma', 'src/db/schema.ts'], dependsOn: [], steps: ['Define tables/models', 'Add relationships', 'Add indexes', 'Add constraints'], acceptance: ['Schema is valid', 'Relationships correct'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Create migration', complexity: 'simple', files: ['prisma/migrations/'], dependsOn: [1], steps: ['Generate migration', 'Review SQL', 'Test migration up/down', 'Add seed data'], acceptance: ['Migration runs cleanly', 'Rollback works'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Implement data access layer', complexity: 'moderate', files: ['src/db/', 'src/repositories/'], dependsOn: [2], steps: ['Create repository/service', 'Add CRUD operations', 'Add error handling', 'Add transaction support'], acceptance: ['CRUD works', 'Transactions handle failures'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Write database tests', complexity: 'moderate', files: ['tests/'], dependsOn: [3], steps: ['Set up test database', 'Write integration tests', 'Test edge cases', 'Test concurrent access'], acceptance: ['Tests pass', 'No data leaks between tests'], status: 'pending', source: 'goal' },
    );
  } else if (isDeploy) {
    tasks.push(
      { id: taskId++, title: 'Set up CI/CD pipeline', complexity: 'moderate', files: ['.github/workflows/', 'Dockerfile'], dependsOn: [], steps: ['Create workflow file', 'Add build step', 'Add test step', 'Add deploy step'], acceptance: ['Pipeline runs on push', 'Tests gate deployment'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Configure environment', complexity: 'simple', files: ['.env.example', 'docker-compose.yml'], dependsOn: [1], steps: ['Document required env vars', 'Create docker-compose for local dev', 'Add health check endpoint'], acceptance: ['Local dev works with docker-compose', 'Health check returns 200'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add monitoring and logging', complexity: 'moderate', files: ['src/lib/logger.ts', 'src/middleware/'], dependsOn: [1], steps: ['Set up structured logging', 'Add request logging middleware', 'Configure error tracking', 'Add performance monitoring'], acceptance: ['Logs are structured', 'Errors tracked'], status: 'pending', source: 'goal' },
    );
  } else if (isMonitor) {
    tasks.push(
      { id: taskId++, title: 'Set up error tracking', complexity: 'simple', files: ['src/lib/monitoring.ts', '.env'], dependsOn: [], steps: ['Install Sentry/Datadog/etc', 'Configure error capture', 'Add source maps', 'Set up error grouping'], acceptance: ['Errors captured in dashboard', 'Source maps resolve'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add structured logging', complexity: 'moderate', files: ['src/lib/logger.ts', 'src/middleware/'], dependsOn: [], steps: ['Install logging library (pino/winston)', 'Create logger with context', 'Add request logging middleware', 'Configure log levels per environment'], acceptance: ['Logs are structured JSON', 'Request IDs propagate'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Implement metrics and tracing', complexity: 'moderate', files: ['src/lib/metrics.ts'], dependsOn: [1, 2], steps: ['Add custom metrics (response time, error rate)', 'Implement distributed tracing', 'Create monitoring dashboard', 'Set up alerting thresholds'], acceptance: ['Metrics visible in dashboard', 'Alerts fire correctly'], status: 'pending', source: 'goal' },
      { id: taskId++, title: 'Add performance monitoring', complexity: 'simple', files: ['src/middleware/performance.ts'], dependsOn: [3], steps: ['Track API response times', 'Monitor database query performance', 'Set up performance budgets', 'Create regression alerts'], acceptance: ['Performance data collected', 'Regressions detected'], status: 'pending', source: 'goal' },
    );
  } else if (isPerf) {
    tasks.push(
      { id: taskId++, title: 'Audit current performance', complexity: 'simple', files: [], dependsOn: [], steps: ['Run Lighthouse audit', 'Analyze bundle size', 'Profile runtime performance', 'Identify bottlenecks'], acceptance: ['Baseline metrics recorded', 'Top 3 bottlenecks identified'] },
      { id: taskId++, title: 'Optimize bundle size', complexity: 'moderate', files: ['next.config.js', 'src/'], dependsOn: [1], steps: ['Enable tree shaking', 'Add dynamic imports for heavy components', 'Optimize images and assets', 'Remove unused dependencies'], acceptance: ['Bundle size reduced by >20%', 'No functionality lost'] },
      { id: taskId++, title: 'Add caching and lazy loading', complexity: 'moderate', files: ['src/'], dependsOn: [1], steps: ['Implement API response caching', 'Add lazy loading for below-fold content', 'Configure CDN caching headers', 'Add service worker for offline support'], acceptance: ['Cache hit rate >80%', 'LCP improved'] },
      { id: taskId++, title: 'Verify improvements', complexity: 'simple', files: [], dependsOn: [2, 3], steps: ['Re-run Lighthouse audit', 'Compare before/after metrics', 'Test on slow connections', 'Document optimizations made'], acceptance: ['Lighthouse score improved', 'Core Web Vitals pass'] },
    );
  } else {
    // Generic feature
    tasks.push(
      { id: taskId++, title: 'Plan and design', complexity: 'simple', files: [], dependsOn: [], steps: ['Define requirements', 'Identify affected files', 'Plan implementation approach'], acceptance: ['Requirements documented', 'Approach agreed'] },
      { id: taskId++, title: 'Implement core functionality', complexity: complexity === 'simple' ? 'simple' : 'moderate', files: ['src/'], dependsOn: [1], steps: ['Create necessary files', 'Implement main logic', 'Add error handling', 'Add TypeScript types'], acceptance: ['Core feature works', 'No type errors'] },
      { id: taskId++, title: 'Integration and testing', complexity: 'moderate', files: ['src/', 'tests/'], dependsOn: [2], steps: ['Integrate with existing code', 'Write unit tests', 'Write integration tests'], acceptance: ['Tests pass', 'No regressions'] },
      { id: taskId++, title: 'Polish and documentation', complexity: 'simple', files: ['docs/', 'README.md'], dependsOn: [3], steps: ['Add documentation', 'Update README if needed', 'Code review and cleanup'], acceptance: ['Docs updated', 'Code reviewed'] },
    );
  }

  const complexityScore = tasks.reduce((sum, t) => sum + (t.complexity === 'simple' ? 1 : t.complexity === 'moderate' ? 2 : 3), 0);
  const totalComplexity = complexityScore <= 4 ? 'Simple' : complexityScore <= 8 ? 'Medium' : 'Complex';

  const riskAssessment = {
    high: 0,
    medium: 0,
    low: 0,
  };

  tasks.forEach((task) => {
    if (task.complexity === 'complex') {
      riskAssessment.high++;
    } else if (task.complexity === 'moderate') {
      riskAssessment.medium++;
    } else {
      riskAssessment.low++;
    }
  });

  return {
    id,
    title: goal,
    objective: goal,
    tasks,
    createdAt: new Date().toISOString(),
    totalComplexity,
    source: 'goal',
    progress: { completed: 0, total: tasks.length, percentage: 0 },
  };
}
